import {useState} from "react";
import { Box, Button, Container, Heading, Input, VStack, HStack, Text, Alert, Field } from "@chakra-ui/react";
import {useForm} from "react-hook-form";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import {api} from "../lib/api";
import {sanitizeReturnTo} from "../lib/authRedirect";
import {isNativeApp} from "../lib/native/platform";
import {clearInstance, getInstance, normalizeOrgId, setInstance} from "../lib/native/instance";
import {hydrateCredentials, setCredential} from "../lib/native/credentialStore";
import {configMatchesOrg, loadPublicConfig, PUBLIC_CONFIG_QUERY_KEY} from "../lib/runtimeConfig";
import {beginOrganizationSwitch, SWITCH_BLOCKED_MESSAGE} from "../lib/session/organizerSession";

interface LoginForm {
  email: string;
  password: string;
}

interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  /** [Native B1, NB1-2] Present only for a native-origin request: the `od_` device session token. */
  sessionToken?: string | null;
  sessionExpiresAt?: string | null;
}

const ORG_NOT_FOUND = "We couldn't find that organization. Check the ID with your organizer.";

function deviceLabel(): string {
  return (navigator.userAgent || "device").slice(0, 60);
}

export function LoginPage() {
  const {register, handleSubmit} = useForm<LoginForm>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // [Native B1, NB1-9] The store binary first needs to know which server to sign in to.
  const native = isNativeApp();
  const [orgId, setOrgId] = useState<string | null>(() => getInstance()?.orgId ?? null);
  const [orgInput, setOrgInput] = useState("");
  const [orgError, setOrgError] = useState<string | null>(null);
  const [orgBusy, setOrgBusy] = useState(false);
  const showOrgStep = native && !orgId;

  const submitOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setOrgError(null);
    const id = normalizeOrgId(orgInput);
    if (!id) {
      setOrgError(ORG_NOT_FOUND);
      return;
    }
    setOrgBusy(true);
    try {
      // The config fetch is the existence check: it goes to the new instance's origin. On a
      // 404/network error the instance is cleared again — a bad one is never left selected. A
      // server naming a different organization is not this one (M1).
      await setInstance(id);
      const config = await loadPublicConfig();
      if (!config || !configMatchesOrg(config, id)) {
        await clearInstance();
        setOrgError(ORG_NOT_FOUND);
        return;
      }
      queryClient.setQueryData(PUBLIC_CONFIG_QUERY_KEY, config);
      await hydrateCredentials();
      setOrgId(id);
    } catch {
      await clearInstance().catch(() => {});
      setOrgError(ORG_NOT_FOUND);
    } finally {
      setOrgBusy(false);
    }
  };

  // NB1-13: blocked (and nothing wiped) while visits are queued or a grant credential exists.
  const switchOrganization = async () => {
    setError(null);
    setOrgBusy(true);
    try {
      if ((await beginOrganizationSwitch(queryClient)) === "blocked") {
        setError(SWITCH_BLOCKED_MESSAGE);
        return;
      }
      setOrgInput("");
      setOrgError(null);
      setOrgId(null);
    } finally {
      setOrgBusy(false);
    }
  };

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<AuthResponse>(
        "/auth/login",
        native ? {...data, deviceLabel: deviceLabel()} : data,
      );

      // [Native B1, NB1-1] The od_ token goes ONLY to OS secure storage — never localStorage.
      if (native) {
        const token = response.data.sessionToken;
        if (!token) throw new Error("No session token");
        await setCredential("organizer", token);
      }

      // Store user info (web: token is in HttpOnly cookie set by server)
      localStorage.setItem("auth_user", JSON.stringify(response.data.user));

      // Prefer the destination the user was originally headed to (set by the
      // RequireAuth guard / 401 interceptor); otherwise route by role.
      const returnTo = sanitizeReturnTo(searchParams.get("returnTo"));
      if (returnTo) {
        navigate(returnTo, {replace: true});
      } else {
        // Only Organizer/Admin/SuperAdmin can log in here — canvassers are grant-session
        // identities (Contact + AccessGrant), not Users, and reach /canvasser via /join, not login.
        navigate("/dashboard");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Login failed. Please check your credentials.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="container.sm" py={16}>
      <VStack gap={8} align="stretch">
        <Box textAlign="center">
          <Heading size="xl" mb={2}>
            Sign In
          </Heading>
          <Text color="gray.600">Access your canvassing account</Text>
        </Box>

        <Box bg="surface" p={8} borderRadius="lg" shadow="md">
          {showOrgStep ? (
            <form onSubmit={submitOrg}>
              <VStack gap={4}>
                {!!orgError && (
                  <Alert.Root status="error" borderRadius="md">
                    <Alert.Indicator />
                    {orgError}
                  </Alert.Root>
                )}

                <Field.Root required>
                  <Field.Label>Organization ID</Field.Label>
                  <Input
                    data-testid="login-org-id"
                    value={orgInput}
                    onChange={(e) => setOrgInput(e.target.value)}
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck={false}
                    minH="44px"
                  />
                  <Field.HelperText>Your organizer can tell you this.</Field.HelperText>
                </Field.Root>

                <Button type="submit" colorPalette="brand" width="full" minH="44px" mt={4} loading={orgBusy}>
                  Continue
                </Button>
              </VStack>
            </form>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={4}>
                {!!error && (
                  <Alert.Root status="error" borderRadius="md">
                    <Alert.Indicator />
                    {error}
                  </Alert.Root>
                )}

                {native && orgId && (
                  <HStack justify="space-between" width="full" fontSize="sm" color="fg.muted">
                    <Text>
                      Organization: <Text as="b" color="fg">{orgId}</Text>
                    </Text>
                    <Button
                      variant="ghost"
                      size="sm"
                      colorPalette="brand"
                      minH="44px"
                      onClick={() => void switchOrganization()}
                      loading={orgBusy}
                    >
                      Switch organization
                    </Button>
                  </HStack>
                )}

                <Field.Root required>
                  <Field.Label>Email</Field.Label>
                  <Input
                    data-testid="login-email"
                    type="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    minH="44px"
                    {...register("email")}
                  />
                </Field.Root>

                <Field.Root required>
                  <Field.Label>Password</Field.Label>
                  <Input
                    data-testid="login-password"
                    type="password"
                    minH="44px"
                    {...register("password")}
                  />
                </Field.Root>

                <Button
                  type="submit"
                  colorPalette="brand"
                  width="full"
                  minH="44px"
                  mt={4}
                  loading={isLoading}
                >
                  Sign In
                </Button>
              </VStack>
            </form>
          )}
        </Box>
      </VStack>
    </Container>
  );
}
